import React, { useState, useEffect } from 'react';
import api from '../services/api';

function ContactsPage() {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedContact, setSelectedContact] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newContact, setNewContact] = useState({
    emailAddress: '',
    displayName: '',
    firstName: '',
    lastName: '',
    company: '',
    phone: ''
  });

  useEffect(() => {
    loadContacts();
  }, [searchTerm]);

  const loadContacts = async () => {
    setLoading(true);
    try {
      const response = await api.get('/contacts', {
        params: { searchTerm: searchTerm || undefined }
      });
      setContacts(response.data || []);
    } catch (error) {
      console.error('Failed to load contacts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddContact = async (e) => {
    e.preventDefault();
    try {
      await api.post('/contacts', newContact);
      setShowAddModal(false);
      setNewContact({ emailAddress: '', displayName: '', firstName: '', lastName: '', company: '', phone: '' });
      loadContacts();
    } catch (error) {
      console.error('Failed to add contact:', error);
    }
  };

  const handleDeleteContact = async (contactId) => {
    if (window.confirm('Are you sure you want to delete this contact?')) {
      try {
        await api.delete(`/contacts/${contactId}`);
        setSelectedContact(null);
        loadContacts();
      } catch (error) {
        console.error('Failed to delete contact:', error);
      }
    }
  };

  const getInitials = (contact) => {
    if (contact.displayName) {
      return contact.displayName.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
    }
    if (contact.firstName || contact.lastName) {
      return `${contact.firstName?.[0] || ''}${contact.lastName?.[0] || ''}`.toUpperCase();
    }
    return contact.emailAddress?.[0]?.toUpperCase() || '?';
  };

  return (
    <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
      {/* Contacts List */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', padding: '20px' }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h2 style={{ fontSize: '20px', fontWeight: 600 }}>Contacts</h2>
          <button className="btn btn-primary" onClick={() => setShowAddModal(true)}>
            + Add Contact
          </button>
        </div>

        {/* Search */}
        <div style={{ marginBottom: '20px' }}>
          <input
            type="text"
            className="form-input"
            placeholder="Search contacts..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        {/* Contacts Grid */}
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
            <div className="loading-spinner"></div>
          </div>
        ) : contacts.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">👥</div>
            <div className="empty-state-title">No contacts</div>
            <div className="empty-state-text">
              Add your first contact to get started.
            </div>
          </div>
        ) : (
          <div className="contacts-grid">
            {contacts.map(contact => (
              <div 
                key={contact.contactId} 
                className="contact-card"
                onClick={() => setSelectedContact(contact)}
                style={{ cursor: 'pointer' }}
              >
                <div className="contact-avatar">{getInitials(contact)}</div>
                <div className="contact-name">
                  {contact.displayName || `${contact.firstName || ''} ${contact.lastName || ''}`.trim() || contact.emailAddress}
                </div>
                <div className="contact-email">{contact.emailAddress}</div>
                {contact.company && (
                  <div style={{ fontSize: '13px', color: 'var(--text-muted)', marginTop: '4px' }}>
                    {contact.company}
                  </div>
                )}
                {contact.emailCount > 0 && (
                  <div style={{ fontSize: '12px', color: 'var(--accent-primary)', marginTop: '8px' }}>
                    {contact.emailCount} emails
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Contact Detail Panel */}
      {selectedContact && (
        <div className="ai-panel">
          <div className="ai-panel-header">
            <span className="ai-panel-title">Contact Details</span>
            <button 
              onClick={() => setSelectedContact(null)}
              style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer' }}
            >
              ✕
            </button>
          </div>

          <div style={{ padding: '20px', textAlign: 'center' }}>
            <div className="contact-avatar" style={{ width: '80px', height: '80px', fontSize: '32px', margin: '0 auto 16px' }}>
              {getInitials(selectedContact)}
            </div>
            <h3 style={{ fontSize: '18px', fontWeight: 600, marginBottom: '4px' }}>
              {selectedContact.displayName || `${selectedContact.firstName || ''} ${selectedContact.lastName || ''}`.trim()}
            </h3>
            <p style={{ color: 'var(--text-muted)', fontSize: '14px' }}>{selectedContact.emailAddress}</p>
          </div>

          <div style={{ padding: '0 20px 20px' }}>
            {selectedContact.company && (
              <div style={{ marginBottom: '12px' }}>
                <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '4px' }}>Company</div>
                <div>{selectedContact.company}</div>
              </div>
            )}
            {selectedContact.jobTitle && (
              <div style={{ marginBottom: '12px' }}>
                <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '4px' }}>Job Title</div>
                <div>{selectedContact.jobTitle}</div>
              </div>
            )}
            {selectedContact.phone && (
              <div style={{ marginBottom: '12px' }}>
                <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '4px' }}>Phone</div>
                <div>{selectedContact.phone}</div>
              </div>
            )}
            {selectedContact.lastContactUtc && (
              <div style={{ marginBottom: '12px' }}>
                <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '4px' }}>Last Contact</div>
                <div>{new Date(selectedContact.lastContactUtc).toLocaleDateString()}</div>
              </div>
            )}
          </div>

          <div style={{ padding: '20px', borderTop: '1px solid var(--border-color)', display: 'flex', gap: '10px' }}>
            <button className="btn btn-primary" style={{ flex: 1 }}>
              ✉️ Email
            </button>
            <button 
              className="btn btn-secondary" 
              onClick={() => handleDeleteContact(selectedContact.contactId)}
            >
              🗑️
            </button>
          </div>
        </div>
      )}

      {/* Add Contact Modal */}
      {showAddModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'var(--card-bg)',
            borderRadius: '12px',
            padding: '24px',
            width: '100%',
            maxWidth: '400px'
          }}>
            <h3 style={{ marginBottom: '20px', fontSize: '18px', fontWeight: 600 }}>Add Contact</h3>
            <form onSubmit={handleAddContact}>
              <div className="form-group">
                <label className="form-label">Email *</label>
                <input
                  type="email"
                  className="form-input"
                  value={newContact.emailAddress}
                  onChange={(e) => setNewContact({ ...newContact, emailAddress: e.target.value })}
                  required
                />
              </div>
              <div style={{ display: 'flex', gap: '12px' }}>
                <div className="form-group" style={{ flex: 1 }}>
                  <label className="form-label">First Name</label>
                  <input
                    type="text"
                    className="form-input"
                    value={newContact.firstName}
                    onChange={(e) => setNewContact({ ...newContact, firstName: e.target.value })}
                  />
                </div>
                <div className="form-group" style={{ flex: 1 }}>
                  <label className="form-label">Last Name</label>
                  <input
                    type="text"
                    className="form-input"
                    value={newContact.lastName}
                    onChange={(e) => setNewContact({ ...newContact, lastName: e.target.value })}
                  />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Company</label>
                <input
                  type="text"
                  className="form-input"
                  value={newContact.company}
                  onChange={(e) => setNewContact({ ...newContact, company: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Phone</label>
                <input
                  type="tel"
                  className="form-input"
                  value={newContact.phone}
                  onChange={(e) => setNewContact({ ...newContact, phone: e.target.value })}
                />
              </div>
              <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
                <button type="button" className="btn btn-secondary" onClick={() => setShowAddModal(false)} style={{ flex: 1 }}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" style={{ flex: 1 }}>
                  Add Contact
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default ContactsPage;

